/**
 * Performance Optimizer for Way Coffee Roasters
 * This file contains optimized JavaScript loading and execution strategies
 */

class PerformanceOptimizer {
  constructor() {
    this.isLoaded = false;
    this.pendingTasks = [];
    this.observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };
  }

  // Lazy load non-critical JavaScript
  lazyLoadScript(src, callback) {
    if (this.isScriptLoaded(src)) return;
    
    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    script.onload = callback;
    document.head.appendChild(script);
  }

  // Check if script is already loaded
  isScriptLoaded(src) {
    return document.querySelector(`script[src="${src}"]`) !== null;
  }

  // Intersection Observer for lazy loading
  createIntersectionObserver(callback) {
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers
      callback();
      return;
    }

    return new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          callback(entry.target);
        }
      });
    }, this.observerOptions);
  }

  // Debounce function for performance
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Throttle function for scroll events
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  // Optimize DOM queries with caching
  createQueryCache() {
    const cache = new Map();
    return {
      get: (selector) => {
        if (cache.has(selector)) {
          return cache.get(selector);
        }
        const element = document.querySelector(selector);
        cache.set(selector, element);
        return element;
      },
      getAll: (selector) => {
        const cacheKey = `all-${selector}`;
        if (cache.has(cacheKey)) {
          return cache.get(cacheKey);
        }
        const elements = document.querySelectorAll(selector);
        cache.set(cacheKey, elements);
        return elements;
      },
      clear: () => cache.clear()
    };
  }

  // Initialize performance optimizations
  init() {
    if (this.isLoaded) return;
    
    this.queryCache = this.createQueryCache();
    this.setupLazyLoading();
    this.optimizeScrollEvents();
    this.isLoaded = true;
  }

  // Setup lazy loading for images and components
  setupLazyLoading() {
    // Lazy load images
    const imageObserver = this.createIntersectionObserver((img) => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        img.classList.add('loaded');
      }
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });

    // Lazy load custom filters
    const filterObserver = this.createIntersectionObserver((container) => {
      if (container.dataset.lazyLoad) {
        this.loadCustomFilters(container);
      }
    });

    document.querySelectorAll('[data-lazy-load="custom-filters"]').forEach(container => {
      filterObserver.observe(container);
    });
  }

  // Load custom filters only when needed
  loadCustomFilters(container) {
    if (container.dataset.loaded) return;
    
    // Load custom filter script
    this.lazyLoadScript('/assets/custom-filter.js', () => {
      container.dataset.loaded = 'true';
      // Initialize filters
      if (window.CustomFilter) {
        window.CustomFilter.initializeAll();
      }
    });
  }

  // Optimize scroll events
  optimizeScrollEvents() {
    const throttledScroll = this.throttle(() => {
      // Only run scroll-dependent code here
      this.handleScroll();
    }, 16); // ~60fps

    window.addEventListener('scroll', throttledScroll, { passive: true });
  }

  handleScroll() {
    // Optimized scroll handling
    const scrollY = window.scrollY;
    
    // Update sticky header
    const header = this.queryCache.get('.header');
    if (header) {
      header.classList.toggle('scrolled', scrollY > 100);
    }
  }

  // Preload critical resources
  preloadCriticalResources() {
    const criticalScripts = [
      '/assets/facets.js',
      '/assets/product-form.js'
    ];

    criticalScripts.forEach(script => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = script;
      link.as = 'script';
      document.head.appendChild(link);
    });
  }
}

// Initialize performance optimizer
const perfOptimizer = new PerformanceOptimizer();

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    perfOptimizer.init();
  });
} else {
  perfOptimizer.init();
}

// Preload critical resources immediately
perfOptimizer.preloadCriticalResources();

// Export for global use
window.PerformanceOptimizer = perfOptimizer;
